import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    if (this.hasAttribute('data-preserve-media-order')) {
      target?.addEventListener('click', this.#handleColorClick, { signal });
    }
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.hasAttribute('data-preserve-media-order') && this.dataset.initialMediaId) {
      const mediaId = this.dataset.initialMediaId;
      customElements.whenDefined('slideshow-component').then(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (this.isConnected) this.slideshow?.select({ id: mediaId }, undefined, { animate: false });
          });
        });
      });
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /** @param {MouseEvent} event */
  #handleColorClick = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    if (!input.closest('.variant-option--swatches') || input.dataset.currentChecked !== 'true') return;
    if (input.closest('variant-picker')?.dataset.productId !== this.dataset.productId) return;

    const mediaId = this.dataset.variantMediaId;
    if (mediaId && this.querySelector(`slideshow-slide[slide-id="${mediaId}"]`)) {
      this.slideshow?.select({ id: mediaId }, undefined, { animate: false });
    }
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    if (this.hasAttribute('data-preserve-media-order') && !event.detail.data.newProduct) {
      if (String(event.detail.data.productId) !== this.dataset.productId) return;

      const mediaId = String(event.detail.resource?.featured_media?.id ?? '');
      if (!mediaId || mediaId === this.dataset.variantMediaId) return;

      if (this.querySelector(`slideshow-slide[slide-id="${mediaId}"]`)) {
        this.slideshow?.select({ id: mediaId }, undefined, { animate: false });
        this.dataset.variantMediaId = mediaId;
        return;
      }
    }

    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
